// src/components/charts/ActivityHeatmap.tsx
//
// ★★★ WAVE 27 — DENSITY, which none of the other forms on this page can show.
//
// ⚠ **WHAT IT ANSWERS THAT A LINE CANNOT.** A line over thirty days shows the
// shape of the month. It does NOT show that the shape is a WEEKLY one — that this
// estate does no work at weekends, that Mondays run heavier than Fridays, that a
// quiet Tuesday is unusual. Laid out as weeks down and weekdays across, the pattern
// is the picture: five dense columns and two empty ones, immediately.
//
// On the seeded estate that is a real property rather than a decoration —
// `seed-demonstration-data.mjs` builds its corpus over BUSINESS days, so eight of
// the thirty days in a window are genuinely zero, and a reader who does not see the
// weekly rhythm concludes the product lost a third of its data.
//
// ⚠ **SEQUENTIAL, ONE HUE, LIGHT TO DARK — never a rainbow.** `color-formula.md`:
// magnitude is a sequential ramp. A viridis or red-to-green scale here would be the
// single most common heat-map mistake, because it encodes ORDER as hue and hue has
// no natural order. The ramp is built from one series token at stepped opacity, so
// it inherits a hue the palette already validated instead of introducing a scale
// nobody checked.
//
// ⚠ **FIVE STEPS, NOT A CONTINUOUS RAMP.** A continuous fill invites a reader to
// compare two cells precisely, which nobody can do with lightness. Buckets make the
// claim honest: "this cell is in the top fifth", not "this cell is 63".
//
// ⚠ **EVERY CELL CARRIES ITS EXACT VALUE IN A `title`, AND ALL OF THEM ARE IN THE
// TABLE.** Colour alone is never the route to a number (§5.3 rules 1 and 2).
import type { ReactElement } from 'react'
import { ChartFrame } from './ChartFrame'
import { MarkNoteBubble, MarkNoteLayer, markProps, useMarkNote } from './mark-interaction'

export interface HeatmapDay {
  /** `YYYY-MM-DD`. */
  readonly date: string
  readonly count: number
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const
const STEPS = 5

/** 0 for an empty day, then 1-5 by quintile of the non-zero values. */
function bucketOf(count: number, max: number): number {
  if (count <= 0) return 0
  if (max <= 0) return 0
  return Math.max(1, Math.min(STEPS, Math.ceil((count / max) * STEPS)))
}

/** Monday-first weekday index, from a `YYYY-MM-DD` read as a calendar date. */
function weekdayIndex(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number)
  // ⚠ UTC, deliberately: these are calendar dates the server already cut in the
  // operating time zone, and re-reading them in the browser's zone would shift a
  // day for anyone west of Singapore.
  const dow = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1)).getUTCDay()
  return (dow + 6) % 7
}

export function ActivityHeatmap({
  title,
  subtitle,
  days,
  dateLabel,
  onSelectDay,
}: {
  readonly title: string
  readonly subtitle: string
  readonly days: readonly HeatmapDay[]
  readonly dateLabel: (iso: string) => string
  /**
   * ★★★ Called with the day the reader pressed — the CELL is the control.
   *
   * ⚠ This replaced a single link under the chart reading *"Open the bills for
   * 27 Aug 2026"*, which could only ever offer the last day in the window. Thirty
   * cells were drawn and exactly one of them was reachable.
   */
  readonly onSelectDay?: (day: HeatmapDay) => void
}): ReactElement {
  const max = days.reduce((highest, day) => Math.max(highest, day.count), 0)

  // Lay the window out in weeks. The first row is padded so every column is a
  // weekday, which is the entire point of the form.
  const rowsOfWeeks: (HeatmapDay | null)[][] = []
  let current: (HeatmapDay | null)[] = []
  days.forEach((day, index) => {
    if (index === 0) current = Array.from({ length: weekdayIndex(day.date) }, () => null)
    current.push(day)
    if (current.length === 7) {
      rowsOfWeeks.push(current)
      current = []
    }
  })
  if (current.length > 0) {
    while (current.length < 7) current.push(null)
    rowsOfWeeks.push(current)
  }

  const { note, show, hide } = useMarkNote()

  return (
    <ChartFrame
      title={title}
      subtitle={subtitle}
      columns={[
        { key: 'date', header: 'Date' },
        { key: 'weekday', header: 'Weekday' },
        { key: 'count', header: 'Bills', numeric: true },
      ]}
      rows={days.map((day) => ({
        date: dateLabel(day.date),
        weekday: WEEKDAYS[weekdayIndex(day.date)] ?? '',
        count: day.count,
      }))}
      emptyMessage="No day in this window carries a value your scope can read."
    >
      <MarkNoteLayer>
      <div className="pt-chart-plot">
        <table className="pt-heatmap" role="img" aria-label={`${title}. ${subtitle}. The figures are available as a table.`}>
          <thead>
            <tr>
              {WEEKDAYS.map((day) => (
                <th key={day} scope="col">
                  {day}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rowsOfWeeks.map((week, weekIndex) => (
              <tr key={`week-${weekIndex}`}>
                {week.map((day, dayIndex) => (
                  <td key={day?.date ?? `pad-${weekIndex}-${dayIndex}`}>
                    {day === null ? (
                      <span className="pt-heat-cell pt-heat-pad" />
                    ) : (
                      <span
                        {...markProps({
                          className: `pt-heat-cell pt-heat-${bucketOf(day.count, max)}`,
                          note: {
                            label: dateLabel(day.date),
                            value: `${day.count.toLocaleString('en-SG')} ${day.count === 1 ? 'bill' : 'bills'}`,
                          },
                          /*
                           * ⚠ A DAY WITH NOTHING ON IT IS NOT PRESSABLE. The filtered
                           * list would open empty, which reads as a broken link rather
                           * than as an empty day — and the cell's own shade already
                           * says the day is empty.
                           */
                          ...(onSelectDay === undefined || day.count === 0
                            ? {}
                            : { onSelect: () => onSelectDay(day) }),
                          show,
                          hide,
                        })}
                      >
                        <span className="sr-only">
                          {dateLabel(day.date)}: {day.count.toLocaleString('en-SG')}
                        </span>
                        {/*
                          ⚠ NO `title` ATTRIBUTE. It made the browser draw its own
                          native tooltip on top of the rendered note — two labels
                          for one cell. The `sr-only` span above is what assistive
                          technology reads.
                        */}
                        <span aria-hidden="true" />
                      </span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        {/*
          The scale, stated. A sequential ramp with no key is a picture whose
          darkness means whatever the reader assumes it means.
        */}
        <p className="pt-heat-key" aria-hidden="true">
          <span>None</span>
          {[0, 1, 2, 3, 4, 5].map((step) => (
            <span key={step} className={`pt-heat-cell pt-heat-${step}`} />
          ))}
          <span>{max.toLocaleString('en-SG')} in a day</span>
        </p>
      </div>
      <MarkNoteBubble note={note} />
      </MarkNoteLayer>
    </ChartFrame>
  )
}
